import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type MouseEvent } from 'react';
import {
  backyardPositions,
  resultsPaceLive,
  type CoursePosition,
  type ResultsStatus,
  type TickerState,
} from '@rabbitfuel/shared';
import { headingAt, nearestFraction, pointAt, type CourseShape } from '../lib/course.ts';

/** So oft rueckt ein Punkt weiter -- bei 2-3 m/s ein paar Meter, auf der Karte ein Pixel. */
const STEP_MS = 3000;

/**
 * Die geschaetzten Stellen aller Laeufer, alle paar Sekunden neu gerechnet --
 * nur solange die Uhr laeuft. Steht sie, stehen auch die Punkte.
 *
 * `results` entscheidet, ob die „Runde ⌀" aus RACE RESULT gilt
 * (`resultsPaceLive`): antwortet die Zeitnahme nicht mehr, rechnen die Punkte
 * mit den Messungen der Regie weiter. Den Zustand bekommt jeder im Raum.
 */
export function useCoursePositions(ticker: TickerState, results?: ResultsStatus | null): CoursePosition[] {
  const [now, setNow] = useState(() => Date.now());
  const running = ticker.clock.running;
  useEffect(() => {
    if (!running) return;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), STEP_MS);
    return () => window.clearInterval(timer);
  }, [running]);
  // Gegen die echte Uhrzeit: bei stehender Uhr bliebe `now` sonst stehen,
  // und ein Ausfall der Zeitnahme fiele nie auf.
  const paced = results ? resultsPaceLive(results, Date.now()) : false;
  return useMemo(() => backyardPositions(ticker, now, paced), [ticker, now, paced]);
}

/** Rand um die Linie in Kartenkoordinaten -- Schilder am Rand ragen sonst ueber das Kaestchen. */
export const COURSE_PAD = 8;
/** An diesen Stellen der Runde zeigt ein Pfeil die Laufrichtung. */
const ARROWS = [0.2, 0.45, 0.7];
/** So weit schaut die Ziellinie auf die Strecke davor und dahinter -- etwa so weit, wie sie lang ist. */
const FLAG_REACH = 5;

/** Wie gross ein Kartenpunkt in Pixeln ist und wie gross die Schrift der Schilder. */
interface Metrics {
  unitPx: number;
  fontPx: number;
}

interface Marker {
  /** Wer hier steht, vorderste Person zuerst. */
  members: CoursePosition[];
  x: number;
  y: number;
  /** Breite des Schilds, wie es gezeichnet wird. */
  w: number;
  /** Breite, mit der es Nachbarn einsammelt: die des ersten Mitglieds. */
  reach: number;
  label: string;
}

const byBib = (a: CoursePosition, b: CoursePosition) => a.bib.localeCompare(b.bib, 'de', { numeric: true });

/** `7·16·23`, bei mehr als drei `7·16 +6` -- das Schild bleibt kurz. */
const markerLabel = (members: CoursePosition[]) => {
  const bibs = [...members].sort(byBib).map((p) => p.bib || '?');
  return bibs.length <= 3 ? bibs.join('·') : `${bibs.slice(0, 2).join('·')} +${bibs.length - 2}`;
};

/**
 * Startnummern als Schilder auf der Linie. Wer so dicht beisammen ist, dass
 * sich die Schilder ueberdecken wuerden, teilt sich eines -- das Feld laeuft
 * in den ersten Minuten jeder Stunde beisammen, und mit der Standardzeit
 * stehen ohnehin alle an derselben Stelle. Neben die Linie geschoben stuende
 * eine Nummer dort, wo niemand laeuft.
 *
 * Die Schilder haben eine feste Schriftgroesse und wachsen nicht mit der
 * Karte. Ob sie sich ueberdecken, haengt also davon ab, wie gross die Karte
 * gerade gezeichnet ist -- deshalb wird mit gemessenen Pixeln gerechnet.
 */
function placeMarkers(shape: CourseShape, positions: CoursePosition[], { unitPx, fontPx }: Metrics): Marker[] {
  const height = (1.5 * fontPx) / unitPx;
  const width = (label: string) => ((label.length * 0.62 + 1.1) * fontPx) / unitPx;
  const markers: Marker[] = [];
  const onCourse = positions.filter((p) => !p.back).sort((a, b) => b.fraction - a.fraction);
  for (const p of onCourse) {
    const { x, y } = pointAt(shape, p.fraction);
    const w = width(p.bib || '?');
    // Verglichen wird mit der Breite des ersten Mitglieds, nicht der ganzen
    // Gruppe -- sonst sammelte ein wachsendes Schild auf einer schmalen Karte
    // nach und nach alle Nachbarn ein.
    const hit = markers.find((m) => Math.abs(m.x - x) < (m.reach + w) / 2 && Math.abs(m.y - y) < height);
    if (hit) {
      hit.members.push(p);
      hit.label = markerLabel(hit.members);
      hit.w = width(hit.label);
    } else {
      markers.push({ members: [p], x, y, w, reach: w, label: markerLabel([p]) });
    }
  }
  // Im Kaestchen bleiben: ein breites Schild am Rand ragte sonst in den Text daneben.
  const inside = (value: number, half: number, size: number) =>
    half * 2 >= size + COURSE_PAD * 2 ? size / 2 : Math.min(size + COURSE_PAD - half, Math.max(half - COURSE_PAD, value));
  return markers.map((m) => ({
    ...m,
    x: inside(m.x, m.w / 2, shape.width),
    y: inside(m.y, height / 2, shape.height),
  }));
}

/**
 * Die Runde als Linie mit den Startnummern darauf. Im Bild (StreamOverlay)
 * nur zum Ansehen, in der Regie zum Antippen: eine Nummer waehlt die Person,
 * eine Stelle auf der Linie setzt sie dorthin.
 *
 * Die Linie ist SVG und waechst mit dem Kaestchen, die Schilder sind HTML
 * darueber -- so bleiben die Nummern auch auf einer kleinen Karte lesbar.
 */
export function CourseMap({
  shape,
  positions,
  selectedId,
  onSelect,
  onPlace,
  className,
  style,
}: {
  shape: CourseShape;
  positions: CoursePosition[];
  selectedId?: string | null;
  onSelect?: (entryId: string) => void;
  onPlace?: (fraction: number) => void;
  className?: string;
  style?: CSSProperties;
}) {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  /** Ein unsichtbares Schild: daran liest sich ab, wie gross die Schrift der Schilder gerade ist. */
  const probeRef = useRef<HTMLSpanElement | null>(null);
  const flagId = `course-flag-${useId().replace(/:/g, '')}`;
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const viewW = shape.width + COURSE_PAD * 2;
  const viewH = shape.height + COURSE_PAD * 2;

  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    const measure = () => {
      const height = box.getBoundingClientRect().height;
      const probe = probeRef.current;
      if (!height || !probe) return;
      const next = { unitPx: height / viewH, fontPx: parseFloat(getComputedStyle(probe).fontSize) };
      setMetrics((prev) =>
        prev && Math.abs(prev.unitPx - next.unitPx) < 0.005 && prev.fontPx === next.fontPx ? prev : next,
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    return () => observer.disconnect();
  }, [viewH]);

  const markers = useMemo(() => (metrics ? placeMarkers(shape, positions, metrics) : []), [shape, positions, metrics]);
  const start = pointAt(shape, 0);
  const startAngle = headingAt(shape, 0, FLAG_REACH);
  const line = shape.xy.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(' ');
  const interactive = Boolean(onSelect || onPlace);
  const hint = positions.find((p) => p.entryId === selectedId)?.fraction;

  const click = (event: MouseEvent<HTMLDivElement>) => {
    const dot = (event.target as Element).closest<HTMLElement>('[data-entry]');
    if (dot?.dataset.entry) return onSelect?.(dot.dataset.entry);
    const matrix = svgRef.current?.getScreenCTM();
    if (!onPlace || !matrix) return;
    const pt = new DOMPoint(event.clientX, event.clientY).matrixTransform(matrix.inverse());
    onPlace(nearestFraction(shape, pt.x, pt.y, hint));
  };

  return (
    <div
      ref={boxRef}
      className={`course-map${interactive ? ' course-map--interactive' : ''}${className ? ` ${className}` : ''}`}
      style={{ aspectRatio: `${viewW} / ${viewH}`, ...style }}
      onClick={interactive ? click : undefined}
      role="img"
      aria-label={`Strecke mit ${positions.filter((p) => !p.back).length} Startnummern`}
    >
      <svg ref={svgRef} className="course-map__svg" viewBox={`${-COURSE_PAD} ${-COURSE_PAD} ${viewW} ${viewH}`}>
        <defs>
          {/* Schachbrett fuer die Start- und Ziellinie. */}
          <pattern id={flagId} width="2.4" height="2.4" patternUnits="userSpaceOnUse">
            <rect width="2.4" height="2.4" fill="#fff" />
            <rect width="1.2" height="1.2" fill="#0b0c0e" />
            <rect x="1.2" y="1.2" width="1.2" height="1.2" fill="#0b0c0e" />
          </pattern>
        </defs>
        {/* Erst ein dunkler Saum, dann die Linie -- so traegt sie auf hellem wie dunklem Bild. */}
        <polyline className="course-map__halo" points={line} />
        <polyline className="course-map__line" points={line} />
        {/* Laufrichtung: kleine Pfeile auf der Linie. */}
        {ARROWS.map((f) => {
          const { x, y, angle } = pointAt(shape, f);
          return (
            <path
              key={f}
              className="course-map__arrow"
              d="M -1.6 -1.7 L 0.9 0 L -1.6 1.7"
              transform={`translate(${x.toFixed(2)} ${y.toFixed(2)}) rotate(${((angle * 180) / Math.PI).toFixed(1)})`}
            />
          );
        })}
        {/* Start und Ziel: ein Schachbrett-Balken quer zur Linie, wie man sie dort sieht. */}
        <rect
          className="course-map__flag"
          x={-1.2}
          y={-4.8}
          width={2.4}
          height={9.6}
          fill={`url(#${flagId})`}
          transform={`translate(${start.x.toFixed(2)} ${start.y.toFixed(2)}) rotate(${((startAngle * 180) / Math.PI).toFixed(1)})`}
        />
      </svg>
      <span ref={probeRef} className="course-map__dot course-map__probe" aria-hidden>
        0
      </span>
      {markers.map(({ members, x, y, label }) => {
        const selected = members.some((p) => p.entryId === selectedId);
        const guess = members.every((p) => !p.measured);
        return (
          <span
            key={members[0].entryId}
            data-entry={(members.find((p) => p.entryId === selectedId) ?? members[0]).entryId}
            className={`course-map__dot${selected ? ' is-selected' : ''}${guess ? ' is-guess' : ''}`}
            style={{ left: `${((x + COURSE_PAD) / viewW) * 100}%`, top: `${((y + COURSE_PAD) / viewH) * 100}%` }}
            title={members.map((p) => `${p.bib} ${p.name}`.trim()).join(', ')}
          >
            {label}
          </span>
        );
      })}
    </div>
  );
}
